import { useCallback, useEffect, useState } from "react";
import { CircularProgress, Button } from "@mui/material";
import { ItemBuild, useCoState } from "./CoState";
import { Filter } from "./CoQuery";
import CoFilterSelect from "./CoFilterSelect";
import CoDataTable from "./CoDataTable";
import CoAdvancedFilters from "./CoAdvancedFilters";
import { useRouter } from "next/router";
import { use as useScreenSize } from "common/src/atoms/ScreenAtom.bs";
import { Provider as SetProvider, fromNum } from "common/src/data/TftSet.bs";
import * as T from "common/src/bindings/Translation2";

export default function CombosPage({
  path,
  exaltedGroups,
}: {
  path?: string;
  exaltedGroups: Array<Array<string>>;
}) {
  const router = useRouter();
  const [
    state,
    dispatch,
    includeFilterOptions,
    excludeFilterOptions,
    getMatches,
    getExcludeMatches,
  ] = useCoState(path);
  const screenSize = useScreenSize();

  const onExpand = useCallback(
    (filter: Filter) => dispatch(["add_filter", filter]),
    [dispatch],
  );
  const onSetBuild = useCallback(
    (build: ItemBuild) => dispatch(["set_build", build]),
    [dispatch],
  );

  const set = state.gameMode === "temp" ? 101 : 140;

  return (
    <div className="flex flex-col items-center flex-shrink-1">
      <div className="p-4 mt-8 bg-bg sm:w-[600px] flex items-center">
        <h2 className="text-2xl">{T.common("Advanced Explorer")}</h2>
        <div className="ml-auto">
          <Button
            variant="text"
            color="primary"
            onClick={(_) => router.push("/explorer")}
          >
            Switch to basic
          </Button>
        </div>
      </div>
      <CoAdvancedFilters
        dispatch={dispatch}
        state={state}
        exaltedGroups={exaltedGroups}
      />
      <SetProvider.make value={fromNum(set)}>
        <div className="flex flex-col items-center xl:flex-row xl:justify-start xl:items-start gap-8 justify-center mt-6">
          <div className="w-[290px] md:w-full xl:w-[290px] p-4 self-start rounded bg-bg flex flex-col flex-wrap xl:flex-col gap-6 justify-center">
            <div className="flex flex-col gap-8 md:flex-row md:gap-24 xl:flex-col xl:gap-10">
              <div>
                <div className="pb-6 flex items-center justify-between">
                  <h4 className="text-xl font-montserrat font-medium leading-none">
                    {T.common("Filters")}
                  </h4>
                  <Button
                    size="small"
                    onClick={(_) => dispatch(["set_all_filters", []])}
                  >
                    {T.common("Clear All")}
                  </Button>
                </div>
                <div className="flex flex-col gap-8">
                  {Array.from({ length: state.filters.length + 1 }).map(
                    (_, idx) => (
                      <CoFilterSelect
                        key={idx}
                        idx={idx}
                        value={state.filters[idx] || null}
                        options={includeFilterOptions}
                        dispatch={dispatch}
                        getMatches={getMatches}
                        allFilters={state.filters}
                        disabled={state.filters.length < idx}
                      />
                    ),
                  )}
                </div>
              </div>
              <div>
                <div className="pb-6 flex justify-between items-center">
                  <h4 className="text-xl font-montserrat font-medium leading-none">
                    {T.common("Exclude")}
                  </h4>
                  <Button
                    size="small"
                    onClick={(_) => dispatch(["set_all_exclude_filters", []])}
                  >
                    {T.common("Clear All")}
                  </Button>
                </div>
                <div className="flex flex-col gap-8">
                  {Array.from({ length: state.excludeFilters.length + 1 }).map(
                    (_, idx) => (
                      <CoFilterSelect
                        key={idx}
                        exclude
                        idx={idx}
                        value={state.excludeFilters[idx] || null}
                        options={excludeFilterOptions}
                        dispatch={dispatch}
                        getMatches={getExcludeMatches}
                        allFilters={state.filters}
                        disabled={state.excludeFilters.length < idx}
                      />
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-bg rounded w-screen max-w-[800px] min-h-[240px] relative">
            <CoDataTable
              queryData={state.queryData}
              onExpand={onExpand}
              onSetBuild={onSetBuild}
              dispatch={dispatch}
              screenSize={screenSize}
              exaltedGroups={exaltedGroups}
            />
            {state.loading ? (
              <div className="flex pt-24 justify-center z-50 absolute top-0 left-0 right-0 bottom-0 bg-[#11111155]">
                <CircularProgress size={60} />
              </div>
            ) : null}
          </div>
        </div>
      </SetProvider.make>
    </div>
  );
}
